import { memo, ComponentType, Suspense, lazy } from "react";
import PixelFacebook from "../components/PixelFacebook";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

import { AuthProvider } from "../providers/Auth";
import LayoutProvider from "../providers/LayoutProvider";
import SubscriptionProvider from "../providers/Subscription";
import Themes from "../components/Theme";

import RainbowLayout from "../components/Layout/RainbowLayout";
import DashboardLayout from "../components/Layout/DashboardLayout";
import ProfileLayout from "../components/Layout/ProfileLayout";
import SimpleLayout from "../components/Layout/SimpleLayout";
import MainLayout from "../components/Layout/MainLayout";
import PrivateRoute from "./../components/PrivateRoute";
import ScrollToTop from "../helpers/ScrollToTop";
import { FileUploadProgressProvider } from "../providers/FileUploader";
import ModulesProvider from "../providers/ModulesProvider";
import LogoLoader from "../components/LogoLoader";
import { useSelector, useDispatch } from "react-redux";
import { setCurrentPage } from "../store/actions/page";
import { useEffect, useState } from "react";
import type { Expert } from "../types";
import constants from "../constants";

// Critical components - Load immediately (small or always needed)
import NotFound from "./NotFound";

// Lazy loaded components - Split into separate chunks
const Group = lazy(() => import("./Group"));
const GroupsList = lazy(() => import("./Group/List"));
const Manteinance = lazy(() => import("./Manteinance"));
const NewsletterUnsubscribe = lazy(() => import("./NewsletterUnsubscribe"));

// Subscriber Pages
const SubscriberSignIn = lazy(() => import("./Subscriber/SignIn"));
const SubscriberSignUp = lazy(() => import("./Subscriber/SignUp"));

// Expert Pages
const ExpertSignIn = lazy(() => import("./Expert/SignIn"));

// Congreso Pages
const CongresoLanding = lazy(() => import("./Congreso/Landing"));
const CongresoTelegram = lazy(() => import("./Congreso/Telegram"));

// Auth Pages
const AuthChangePassword = lazy(() => import("./Auth/ChangePassword"));
const AuthPasswordRecovery = lazy(() => import("./Auth/PasswordRecovery"));
const AuthPasswordRecoveryEmailSent = lazy(() => import("./Auth/PasswordRecoveryEmailSent"));

// Checkout Pages
const CheckoutSuccess = lazy(() => import("./Checkout/Success"));
const CheckoutError = lazy(() => import("./Checkout/Error"));
const CheckoutFreeSubscription = lazy(() => import("./Checkout/FreeSubscription"));
const CheckoutPaidSubscription = lazy(() => import("./Checkout/PaidSubscription"));
const CheckoutRetryPaidSubscription = lazy(() => import("./Checkout/RetryPaidSubscription"));
const CheckoutChangePagymentMethod = lazy(() => import("./Checkout/ChangePagymentMethod"));

// Terms Pages
const CreatorsTermsOfServices = lazy(() => import("./Terms/CreatorsTermsOfServices"));

// Analytics
const Analytics = lazy(() => import("./components/Analytics"));

// Profile Pages - Lazy loaded
const ProfileWelcome = lazy(() => import("./Profile/Welcome"));
const ProfileCompleteProfile = lazy(() => import("./Profile/CompleteProfile"));
const ProfileSubscriptions = lazy(() => import("./Profile/Subscriptions"));
const ProfileSubscriptionsManager = lazy(() => import("./Profile/SubscriptionsManager"));
const ProfileMyAccount = lazy(() => import("./Profile/MyAccount"));
const ProfileNotifications = lazy(() => import("./Profile/Notifications"));
const ProfileAnalytics = lazy(() => import("./Profile/Analytics"));
const ProfileUpdateSubscriptionPaymentMethod = lazy(() => import("./Profile/UpdateSubscriptionPaymentMethod"));
const ProfileGateways = lazy(() => import("./Profile/Gateways"));

// Dashboard Pages - Lazy loaded
// Community
const CommunityPreview = lazy(() => import("./Dashboard/Community/Preview"));
const CommunityMembers = lazy(() => import("./Dashboard/Community/Members"));
const CommunityTabs = lazy(() => import("./Dashboard/Community/components/Tabs"));

// Lead Magnets
const LeadMagnetList = lazy(() => import("./Dashboard/LeadMagnets/List"));
const LeadMagnetNew = lazy(() => import("./Dashboard/LeadMagnets/New"));
const LeadMagnetEdit = lazy(() => import("./Dashboard/LeadMagnets/Edit"));

// Community Events
const CommunityEventList = lazy(() => import("./Dashboard/Community/Event/List"));
const CommunityEventNew = lazy(() => import("./Dashboard/Community/Event/New"));
const CommunityEventPreview = lazy(() => import("./Dashboard/Community/Event/Preview"));
const CommunityEventEdit = lazy(() => import("./Dashboard/Community/Event/Edit"));
const CommunityEventTabs = lazy(() => import("./Dashboard/Community/Event/components/Tabs"));

// Membership
const MembershipList = lazy(() => import("./Dashboard/Membership/List"));
const MembershipNew = lazy(() => import("./Dashboard/Membership/New"));
const MembershipPreview = lazy(() => import("./Dashboard/Membership/Preview"));
const MembershipEdit = lazy(() => import("./Dashboard/Membership/Edit"));
const MembershipMembers = lazy(() => import("./Dashboard/Membership/Members"));
const MembershipTabs = lazy(() => import("./Dashboard/Membership/components/Tabs"));

// Experience
const ExperienceList = lazy(() => import("./Dashboard/Experience/List"));
const ExperienceNew = lazy(() => import("./Dashboard/Experience/New"));
const ExperiencePreview = lazy(() => import("./Dashboard/Experience/Preview"));
const ExperienceEdit = lazy(() => import("./Dashboard/Experience/Edit"));
const ExperienceMembers = lazy(() => import("./Dashboard/Experience/Members"));
const ExperienceModules = lazy(() => import("./Dashboard/Experience/Modules"));
const ExperienceTabs = lazy(() => import("./Dashboard/Experience/components/Tabs"));

// Posts (shared)
const PostsList = lazy(() => import("./Dashboard/Posts/List"));
const PostsNew = lazy(() => import("./Dashboard/Posts/New"));
const PostsNewV2 = lazy(() => import("./Dashboard/Posts/NewV2"));
const PostsEdit = lazy(() => import("./Dashboard/Posts/Edit"));
const PostsEditV2 = lazy(() => import("./Dashboard/Posts/EditV2"));

// Sessions
const SessionList = lazy(() => import("./Dashboard/Sessions/List"));
const SessionNew = lazy(() => import("./Dashboard/Sessions/New"));
const SessionDuplicate = lazy(() => import("./Dashboard/Sessions/Duplicate"));
const SessionEdit = lazy(() => import("./Dashboard/Sessions/Edit"));

// Surveys
const SurveyList = lazy(() => import("./Dashboard/Survey/List"));

// Price
const Price = lazy(() => import("./Dashboard/Price"));

// Configuration
const Configuration = lazy(() => import("./Dashboard/Configuration"));
const WelcomeContentPostNew = lazy(() => import("./Dashboard/WelcomeContentPost/New"));
const WelcomeContentPostEdit = lazy(() => import("./Dashboard/WelcomeContentPost/Edit"));

// Coupon
const CouponList = lazy(() => import("./Dashboard/Coupon/List"));
const CouponNew = lazy(() => import("./Dashboard/Coupon/New"));

// Other
const Members = lazy(() => import("./Dashboard/Community/Members"));

// Loading fallback component - Unified and lightweight
const GlobalLoadingFallback = memo(() => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    width: '100%'
  }}>
    <LogoLoader />
  </div>
));
GlobalLoadingFallback.displayName = 'GlobalLoadingFallback';

// Helper to create route components with consistent structure
const createRouteComponent = (
  name: string,
  PageComponent: ComponentType,
  ThemeComponent: typeof Themes.Klouser | typeof Themes.CustomTheme = Themes.Klouser,
  withLayout: boolean = false
) => {
  const Component = memo(() => {
    const content = withLayout ? (
      <RainbowLayout>
        <PageComponent />
      </RainbowLayout>
    ) : (
      <PageComponent />
    );

    return <ThemeComponent>{content}</ThemeComponent>;
  });

  Component.displayName = `${name}Route`;
  return Component;
};

// Public Route Components
const NewsletterUnsubscribeRoute = createRouteComponent(
  "NewsletterUnsubscribe",
  NewsletterUnsubscribe
);

// Private Route Components - None (now using wrappers)

// Custom Theme Route Components
const GroupRoute = createRouteComponent("Group", Group, Themes.CustomTheme);

// GroupsList Route - Special case with Redux connection
const GroupsListRoute = memo(() => (
  <Themes.CustomTheme>
    <RainbowLayout>
      {/* @ts-expect-error - Redux connect injects props (groups, expert, loading, error) automatically */}
      <GroupsList />
    </RainbowLayout>
  </Themes.CustomTheme>
));
GroupsListRoute.displayName = 'GroupsListRoute';

// Error Route Components - Not lazy loaded (always available)
const NotFoundRoute = createRouteComponent("NotFound", NotFound);
const NotFoundWithLayoutRoute = createRouteComponent(
  "NotFoundWithLayout",
  NotFound,
  Themes.Klouser,
  true
);
const ManteinanceRoute = createRouteComponent(
  "Manteinance",
  Manteinance,
  Themes.Klouser,
  true
);

// Expert Routes Wrapper Component
const ExpertRoutesWrapper = memo(() => {
  return (
    <Themes.Klouser>
      <MainLayout>
        <Routes>
          <Route path="signin" element={<ExpertSignIn />} />
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Routes>
      </MainLayout>
    </Themes.Klouser>
  );
});
ExpertRoutesWrapper.displayName = 'ExpertRoutesWrapper';

// Subscriber Routes Wrapper Component
const SubscriberRoutesWrapper = memo(() => {
  return (
    <Themes.Klouser>
      <MainLayout>
        <Routes>
          <Route path="signin" element={<SubscriberSignIn />} />
          <Route path="signup" element={<SubscriberSignUp />} />
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Routes>
      </MainLayout>
    </Themes.Klouser>
  );
});
SubscriberRoutesWrapper.displayName = 'SubscriberRoutesWrapper';

// Congreso Routes Wrapper Component
const CongresoRoutesWrapper = memo(() => {
  return (
    <Themes.Klouser>
      <Routes>
        <Route index element={<CongresoLanding />} />
        <Route
          path="telegram"
          element={
            <MainLayout>
              <CongresoTelegram />
            </MainLayout>
          }
        />
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Routes>
    </Themes.Klouser>
  );
});
CongresoRoutesWrapper.displayName = 'CongresoRoutesWrapper';

// Auth Routes Wrapper Component
const AuthRoutesWrapper = memo(() => {
  return (
    <Themes.CustomTheme>
      <MainLayout>
        <Routes>
          <Route path="password-recovery" element={<AuthPasswordRecovery />} />
          <Route path="change-password" element={<AuthChangePassword />} />
          <Route path="password-recovery-sent" element={<AuthPasswordRecoveryEmailSent />} />
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Routes>
      </MainLayout>
    </Themes.CustomTheme>
  );
});
AuthRoutesWrapper.displayName = 'AuthRoutesWrapper';

// Checkout Routes Wrapper Component
const CheckoutRoutesWrapper = memo(() => {
  const location = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    const page =
      location &&
      constants.pages.filter((page) => {
        return location.pathname.match(page.regex);
      });

    dispatch(setCurrentPage(page[0]));
  }, [location, dispatch]);

  return (
    <Themes.Klouser>
      <Analytics />
      <Routes>
        <Route
          path="free-subscription/:idGroup"
          element={
            <PrivateRoute>
              <CheckoutFreeSubscription />
            </PrivateRoute>
          }
        />
        <Route
          path="success"
          element={
            <PrivateRoute>
              <CheckoutSuccess />
            </PrivateRoute>
          }
        />
        <Route
          path="faliure"
          element={
            <PrivateRoute>
              <RainbowLayout>
                <CheckoutError />
              </RainbowLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="payment"
          element={
            <PrivateRoute>
              <CheckoutPaidSubscription />
            </PrivateRoute>
          }
        />
        <Route
          path="retry-payment"
          element={
            <PrivateRoute>
              <CheckoutRetryPaidSubscription />
            </PrivateRoute>
          }
        />
        <Route
          path="change-payment-method"
          element={<CheckoutChangePagymentMethod />}
        />
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Routes>
    </Themes.Klouser>
  );
});
CheckoutRoutesWrapper.displayName = 'CheckoutRoutesWrapper';

// Terms Routes Wrapper Component
const TermsRoutesWrapper = memo(() => {
  const userInformation = useSelector((state: any) => state.userStore.information.data);
  const [expert, setExpert] = useState<Expert>();

  useEffect(() => {
    if (!!userInformation?.expert) {
      setExpert(userInformation?.expert);
    }
  }, [userInformation]);

  return (
    <Themes.Klouser>
      <Routes>
        <Route index element={<Navigate to="creators/terms-of-services" replace />} />
        <Route
          path="creators/terms-of-services"
          element={
            <RainbowLayout>
              <CreatorsTermsOfServices />
            </RainbowLayout>
          }
        />
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Routes>
    </Themes.Klouser>
  );
});
TermsRoutesWrapper.displayName = 'TermsRoutesWrapper';

// Onboarding Routes Wrapper Component
const OnboardingRoutesWrapper = memo(() => {
  return (
    <Themes.Klouser>
      <Routes>
        <Route
          path="welcome"
          element={
            <SimpleLayout>
              <ProfileWelcome />
            </SimpleLayout>
          }
        />

        <Route
          path="complete-profile"
          element={
            <SimpleLayout>
              <ProfileCompleteProfile />
            </SimpleLayout>
          }
        />

        <Route path="*" element={<Navigate to="/404" replace />} />
      </Routes>
    </Themes.Klouser>
  );
});
OnboardingRoutesWrapper.displayName = 'OnboardingRoutesWrapper';

// Dashboard Routes Wrapper Component
const DashboardRoutesWrapper = memo(() => {
  return (
    <Themes.Klouser>
      <DashboardLayout>
        <Routes>
          {/* Root redirect */}
          <Route index element={<Navigate to="community" replace />} />

          {/* Coupon Routes */}
          <Route path="coupon">
            <Route index element={<Navigate to="list/active" replace />} />
            <Route path="list" element={<Navigate to="active" replace />} />
            <Route path="list/:status" element={<CouponList />} />
            <Route path="new" element={<CouponNew />} />
          </Route>

          {/* Community Routes */}
          <Route path="community">
            <Route index element={<CommunityPreview />} />
            <Route path="members" element={<Suspense fallback={<div style={{ minHeight: '400px' }} />}><CommunityMembers /></Suspense>} />
            <Route path=":idGroup/lead-magnets">
              <Route index element={<Suspense fallback={<LogoLoader />}><LeadMagnetList><CommunityTabs active="library" /></LeadMagnetList></Suspense>} />
              <Route path="new" element={<LeadMagnetNew groupType="community" />} />
              <Route path=":idPost" element={<LeadMagnetEdit />} />
            </Route>
            <Route path=":idGroup/posts/v2/new" element={<PostsNewV2 groupType="community" />} />

            {/* Community Events Routes */}
            <Route path="events">
              <Route index element={<Navigate to="list" replace />} />
              <Route path="list" element={<CommunityEventList />} />
              <Route path="new" element={<CommunityEventNew />} />
              <Route path=":idGroup">
                <Route index element={<CommunityEventPreview />} />
                <Route path="edit" element={<CommunityEventEdit />} />
                <Route path="posts">
                  <Route index element={<Suspense fallback={<LogoLoader />}><PostsList><CommunityEventTabs active="library" hiddenTabs={["modules", "members", "sessions"]} /></PostsList></Suspense>} />
                  <Route path="new" element={<PostsNew groupType="free_experience" />} />
                  <Route path="v2/new" element={<PostsNewV2 groupType="free_experience" />} />
                  <Route path=":idPost" element={<PostsEdit />} />
                  <Route path="v2/:idPost" element={<PostsEditV2 />} />
                </Route>
                <Route path="surveys" element={<Suspense fallback={<LogoLoader />}><SurveyList><CommunityEventTabs active="surveys" hiddenTabs={["modules", "sessions", "members"]} /></SurveyList></Suspense>} />
              </Route>
            </Route>
          </Route>

          {/* Membership Routes */}
          <Route path="membership">
            <Route index element={<Navigate to="list" replace />} />
            <Route path="list" element={<MembershipList />} />
            <Route path="new" element={<MembershipNew />} />
            <Route path=":idGroup">
              <Route index element={<MembershipPreview />} />
              <Route path="edit" element={<MembershipEdit />} />
              <Route path="configuration" element={<Suspense fallback={<LogoLoader />}><Configuration><MembershipTabs active="configuration" /></Configuration></Suspense>} />
              <Route path="welcome-content">
                <Route path="new" element={<WelcomeContentPostNew />} />
                <Route path=":idPost" element={<WelcomeContentPostEdit />} />
              </Route>
              <Route path="members" element={<MembershipMembers />} />
              <Route path="posts">
                <Route index element={<Suspense fallback={<LogoLoader />}><PostsList><MembershipTabs active="library" /></PostsList></Suspense>} />
                <Route path="new" element={<Navigate to="v2/new" replace />} />
                <Route path="v2/new" element={<PostsNewV2 groupType="membership" />} />
                <Route path=":idPost" element={<Navigate to="v2/:idPost" replace />} />
                <Route path="v2/:idPost" element={<PostsEditV2 />} />
              </Route>
              <Route path="sessions">
                <Route index element={<Suspense fallback={<LogoLoader />}><SessionList><MembershipTabs active="sessions" /></SessionList></Suspense>} />
                <Route path="new" element={<SessionNew />} />
                <Route path="duplicate/:sessionId" element={<SessionDuplicate />} />
                <Route path=":session" element={<SessionEdit />} />
              </Route>
              <Route path="surveys" element={<Suspense fallback={<LogoLoader />}><SurveyList><MembershipTabs active="surveys" /></SurveyList></Suspense>} />
              <Route path="price" element={<Suspense fallback={<LogoLoader />}><Price><MembershipTabs active="price" /></Price></Suspense>} />
            </Route>
          </Route>

          {/* Experience Routes */}
          <Route path="experience">
            <Route index element={<Navigate to="list" replace />} />
            <Route path="list" element={<ExperienceList />} />
            <Route path="new" element={<ExperienceNew />} />
            <Route path=":idGroup">
              <Route index element={<ModulesProvider useLocation={null}><ExperiencePreview /></ModulesProvider>} />
              <Route path="edit" element={<ExperienceEdit />} />
              <Route path="configuration" element={<Suspense fallback={<LogoLoader />}><Configuration><ExperienceTabs active="configuration" /></Configuration></Suspense>} />
              <Route path="welcome-content">
                <Route path="new" element={<WelcomeContentPostNew />} />
                <Route path=":idPost" element={<WelcomeContentPostEdit />} />
              </Route>
              <Route path="members" element={<ExperienceMembers />} />
              <Route path="modules" element={<ModulesProvider useLocation={null}><ExperienceModules /></ModulesProvider>} />
              <Route path="posts">
                <Route index element={<Suspense fallback={<LogoLoader />}><PostsList><ExperienceTabs active="library" /></PostsList></Suspense>} />
                <Route path="new" element={<PostsNew groupType="experience" />} />
                <Route path="v2/new" element={<PostsNewV2 groupType="experience" />} />
                <Route path=":idPost" element={<Navigate to="v2/:idPost" replace />} />
                <Route path="v2/:idPost" element={<PostsEditV2 />} />
              </Route>
              <Route path="sessions">
                <Route index element={<Suspense fallback={<LogoLoader />}><SessionList><ExperienceTabs active="sessions" /></SessionList></Suspense>} />
                <Route path="new" element={<SessionNew />} />
                <Route path="duplicate/:sessionId" element={<SessionDuplicate />} />
                <Route path=":session" element={<SessionEdit />} />
              </Route>
              <Route path="surveys" element={<Suspense fallback={<LogoLoader />}><SurveyList><ExperienceTabs active="surveys" /></SurveyList></Suspense>} />
              <Route path="price" element={<Suspense fallback={<LogoLoader />}><Price><ExperienceTabs active="price" /></Price></Suspense>} />
            </Route>
          </Route>

          {/* Survey Routes */}
          <Route path="survey">
            <Route path="list" element={<Navigate to="active" replace />} />
            <Route path="list/:status" element={<SurveyList />} />
          </Route>

          {/* Members Route */}
          <Route path="members" element={<Members />} />

          {/* Profile Routes */}
          <Route path="profile">
            <Route index element={<Navigate to="my-account" replace />} />

            <Route
              path="my-account"
              element={
                <ProfileLayout>
                  <ProfileMyAccount />
                </ProfileLayout>
              }
            />

            <Route
              path="gateways"
              element={
                <ProfileLayout>
                  <ProfileGateways />
                </ProfileLayout>
              }
            />

            <Route
              path="notifications"
              element={
                <ProfileLayout>
                  <ProfileNotifications />
                </ProfileLayout>
              }
            />

            <Route
              path="analytics"
              element={
                <ProfileLayout>
                  <ProfileAnalytics />
                </ProfileLayout>
              }
            />

            <Route
              path="subscriptions"
              element={<Navigate to="/dashboard/profile/subscriptions/active" replace />}
            />
            <Route
              path="subscriptions/:status"
              element={
                <ProfileSubscriptions />
              }
            />

            <Route
              path="manage-subscriptions/update"
              element={
                <RainbowLayout>
                  <ProfileUpdateSubscriptionPaymentMethod />
                </RainbowLayout>
              }
            />

            <Route
              path="manage-subscriptions"
              element={
                <ProfileLayout>
                  <Suspense fallback={<div style={{ minHeight: '400px' }} />}>
                    <ProfileSubscriptionsManager />
                  </Suspense>
                </ProfileLayout>
              }
            />

            <Route path="*" element={<Navigate to="/404" replace />} />
          </Route>

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Routes>
      </DashboardLayout>
    </Themes.Klouser>
  );
});
DashboardRoutesWrapper.displayName = 'DashboardRoutesWrapper';


const Component = () => {
  return (
    <AuthProvider>
      <SubscriptionProvider>
        <FileUploadProgressProvider>
          <LayoutProvider>
            <PixelFacebook />
            <ScrollToTop />
            <Suspense fallback={<GlobalLoadingFallback />}>
              <Routes>
                {/* Public Routes - Specific paths first */}
                <Route path="/" element={<Navigate to="/subscriber/signin" replace />} />
                <Route path="/congreso/*" element={<CongresoRoutesWrapper />} />
                <Route path="/subscriber/*" element={<SubscriberRoutesWrapper />} />
                <Route path="/expert/*" element={<ExpertRoutesWrapper />} />
                <Route path="/terms/*" element={<TermsRoutesWrapper />} />
                <Route path="/checkout/*" element={<CheckoutRoutesWrapper />} />
                <Route path="/unsubscribe" element={<NewsletterUnsubscribeRoute />} />

                {/* Auth Routes */}
                <Route path="/auth/*" element={<AuthRoutesWrapper />} />

                {/* Onboarding Routes - Must come before /profile redirects */}
                <Route
                  path="/onboarding/*"
                  element={
                    <PrivateRoute>
                      <OnboardingRoutesWrapper />
                    </PrivateRoute>
                  }
                />

                {/* Group Routes - Must come before /:username */}
                <Route path="/group/*" element={<GroupRoute />} />

                {/* User Profile Routes - Dynamic username */}
                <Route path="/:username" element={<GroupsListRoute />} />

                {/* Legacy Profile Routes - Redirect to new routes */}
                <Route
                  path="/profile/welcome"
                  element={<Navigate to="/onboarding/welcome" replace />}
                />
                <Route
                  path="/profile/complete-profile"
                  element={<Navigate to="/onboarding/complete-profile" replace />}
                />
                <Route
                  path="/profile/suscriber"
                  element={<Navigate to="/dashboard/profile/subscriptions" replace />}
                />
                <Route
                  path="/profile/my-account"
                  element={<Navigate to="/dashboard/profile/my-account" replace />}
                />
                <Route
                  path="/profile/gateways"
                  element={<Navigate to="/dashboard/profile/gateways" replace />}
                />
                <Route
                  path="/profile/notifications"
                  element={<Navigate to="/dashboard/profile/notifications" replace />}
                />
                <Route
                  path="/profile/analytics"
                  element={<Navigate to="/dashboard/profile/analytics" replace />}
                />
                <Route
                  path="/profile/subscriptions"
                  element={<Navigate to="/dashboard/profile/subscriptions" replace />}
                />
                <Route
                  path="/profile/manage-subscriptions/update"
                  element={<Navigate to="/dashboard/profile/manage-subscriptions/update" replace />}
                />
                <Route
                  path="/profile/manage-subscriptions"
                  element={<Navigate to="/dashboard/profile/manage-subscriptions" replace />}
                />
                <Route
                  path="/profile"
                  element={<Navigate to="/dashboard/profile/my-account" replace />}
                />
                <Route
                  path="/profile/*"
                  element={<Navigate to="/dashboard/profile" replace />}
                />

                {/* Private Routes */}
                <Route
                  path="/dashboard/*"
                  element={
                    <PrivateRoute>
                      <DashboardRoutesWrapper />
                    </PrivateRoute>
                  }
                />

                {/* Error Routes */}
                <Route path="/404" element={<NotFoundWithLayoutRoute />} />
                <Route path="/500" element={<NotFoundWithLayoutRoute />} />
                <Route path="/503" element={<NotFoundWithLayoutRoute />} />
                <Route path="/manteinance" element={<ManteinanceRoute />} />

                {/* Catch-all - Must be last */}
                <Route path="*" element={<NotFoundRoute />} />
              </Routes>
            </Suspense>
          </LayoutProvider>
        </FileUploadProgressProvider>
      </SubscriptionProvider>
    </AuthProvider>
  );
};

export default Component;
